<template>
  <div class="animated fadeIn">
    <div class="card shadow-sm border-0">
      <div class="card-header bg-dark text-white d-flex align-items-center justify-content-between py-2">
        <h6 class="mb-0"><font-awesome-icon icon="clock" /> Scheduled OT Permission</h6>
        <b-button variant="success" size="sm" @click="openAdd">
          <font-awesome-icon icon="plus" /> Schedule OT
        </b-button>
      </div>
      <div class="card-body position-relative">
        <b-alert show variant="info" class="small py-2">
          A scheduled OT names the <strong>shifts</strong> it applies to (tick one or more; each shift is shown with its payroll group).
          The office kiosk shows the <strong>OT IN</strong> and <strong>OT OUT</strong> buttons only on a day with a scheduled OT,
          and only accepts them from employees on a ticked shift, inside the time window. Overtime pay is capped to the window.
          A window can only start at or after the latest <strong>Time Out</strong> of the ticked shifts.
        </b-alert>

        <b-row class="mb-3">
          <b-col md="4">
            <b-form-select v-model="filter_group_id" :options="filterOptions" size="sm" @change="getAll"></b-form-select>
          </b-col>
          <b-col md="8">
            <b-input-group size="sm">
              <b-input-group-prepend>
                <b-input-group-text class="bg-dark text-white"><font-awesome-icon icon="search" /></b-input-group-text>
              </b-input-group-prepend>
              <b-form-input v-model="search" placeholder="Search purpose or date" debounce="400" @update="getAll"></b-form-input>
            </b-input-group>
          </b-col>
        </b-row>

        <b-table
          responsive hover striped bordered small
          :items="list.data" :fields="fields" :busy="loading" show-empty head-variant="dark"
          empty-text="No scheduled OT yet."
        >
          <template v-slot:cell(date)="data">
            <strong>{{ data.item.formatted_date.other_formats.format_1 }}</strong>
          </template>
          <template v-slot:cell(shifts)="data">
            <b-badge v-for="s in data.item.shift_types" :key="s.id" variant="dark" class="mr-1 mb-1">
              <span class="text-white-50">{{ s.payroll_group ? s.payroll_group.name : '-' }} &middot;</span> {{ s.description }}
            </b-badge>
            <span v-if="!data.item.shift_types || !data.item.shift_types.length" class="text-muted small">no shift</span>
          </template>
          <template v-slot:cell(window)="data">
            {{ data.item.formatted_time_from }} &ndash; {{ data.item.formatted_time_to }}
            <small class="text-muted d-block">{{ duration(data.item) }}</small>
          </template>
          <template v-slot:cell(status)="data">
            <b-badge :variant="statusVariant(data.item.status)">{{ data.item.status }}</b-badge>
          </template>
          <template v-slot:cell(created_by)="data">
            <small>{{ data.item.created_by_user ? data.item.created_by_user.full_name : '—' }}</small>
          </template>
          <template v-slot:cell(action)="data">
            <div class="text-center text-nowrap">
              <b-button variant="outline-primary" size="sm" @click="openEdit(data.item)"><font-awesome-icon icon="edit" /></b-button>
              <b-button variant="outline-danger" size="sm" @click="remove(data.item)"><font-awesome-icon icon="minus-circle" /></b-button>
            </div>
          </template>
        </b-table>

        <nav v-if="list.total > 0">
          <b-pagination :disabled="loading" @input="getAll" align="right"
            :total-rows="parseInt(list.total)" :per-page="parseInt(list.per_page)"
            v-model="list.current_page" class="mb-0"></b-pagination>
        </nav>
      </div>
    </div>

    <b-modal v-model="modal" centered size="lg" :hide-footer="true" :title="form.id ? 'Edit Scheduled OT' : 'Schedule OT'">
      <b-form @submit.prevent="save">
        <b-row>
          <!-- Shifts the OT applies to -->
          <b-col md="5">
            <label class="d-block font-weight-bold mb-1">Applies to shifts</label>
            <div class="border rounded p-2 shift-picker">
              <div v-for="g in shiftGroups" :key="g.name" class="mb-2">
                <div class="d-flex align-items-center justify-content-between">
                  <small class="text-uppercase font-weight-bold text-muted">{{ g.name }}</small>
                  <a href="#" class="small" @click.prevent="toggleGroup(g)">{{ allTicked(g) ? 'none' : 'all' }}</a>
                </div>
                <b-form-checkbox
                  v-for="s in g.shifts"
                  :key="s.id"
                  v-model="form.shift_type_ids"
                  :value="s.id"
                  @change="getEarliestStart"
                  class="ml-1"
                >
                  {{ s.description }}
                  <small class="text-muted d-block">{{ hm(s.am_time_in) }} &ndash; {{ hm(s.pm_time_out) }} &middot; out {{ hm(s.pm_time_out) }}</small>
                </b-form-checkbox>
              </div>
              <div v-if="shiftGroups.length === 0" class="text-muted small">No active shift found. Create shifts under Payroll Groups &amp; Shifts first.</div>
            </div>
            <small class="text-muted">{{ form.shift_type_ids.length }} shift(s) ticked</small>
          </b-col>

          <!-- Date, window, purpose -->
          <b-col md="7">
            <b-form-group label="Date">
              <b-form-input type="date" v-model="form.date" required @change="getEarliestStart"></b-form-input>
            </b-form-group>
            <b-row>
              <b-col>
                <b-form-group label="OT allowed from" :description="pm_time_out ? ('Must be at or after ' + pmOutText + ', the latest Time Out of the ticked shifts.') : 'Tick the shifts first.'">
                  <b-form-input type="time" v-model="form.time_from" :min="pm_time_out || undefined" required></b-form-input>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group label="Until" description="OT hours after this are not paid.">
                  <b-form-input type="time" v-model="form.time_to" required></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
            <b-form-group label="Purpose">
              <b-form-input v-model="form.purpose" required maxlength="255" placeholder="e.g. Month-end closing"></b-form-input>
            </b-form-group>
            <b-button type="submit" variant="success" block :disabled="form.shift_type_ids.length === 0"><font-awesome-icon icon="save" /> Save</b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
  import SwalMixin from '@/views/mixins/Swal.js'

  export default {
    name: 'OtSchedules',
    mixins: [SwalMixin],
    data() {
      return {
        loading: true,
        search: '',
        groups: [],
        shifts: [],
        filter_group_id: null,
        list: { data: [], current_page: 1, total: 0, per_page: 1 },
        fields: [
          { key: 'date', label: 'Date' },
          { key: 'shifts', label: 'Applies to' },
          { key: 'window', label: 'OT Window' },
          { key: 'purpose', label: 'Purpose' },
          { key: 'status', label: 'Status' },
          { key: 'created_by', label: 'Scheduled By' },
          { key: 'action', label: '' },
        ],
        modal: false,
        form: { id: null, shift_type_ids: [], date: '', time_from: '17:00', time_to: '20:00', purpose: '' },
        // Latest Time Out (HH:MM) among the ticked shifts; OT may only start from here.
        pm_time_out: '',
      }
    },
    computed: {
      filterOptions() {
        return [{ value: null, text: 'All payroll groups' }]
          .concat(this.groups.map(g => ({ value: g.id, text: g.name })));
      },
      // Active shifts grouped under their payroll group for the checkbox list.
      shiftGroups() {
        const groups = [];
        this.shifts.forEach(s => {
          const name = s.payroll_group ? s.payroll_group.name : 'No payroll group';
          let g = groups.find(x => x.name === name);
          if (!g) { g = { name: name, shifts: [] }; groups.push(g); }
          g.shifts.push(s);
        });
        return groups;
      },
      pmOutText() {
        if (!this.pm_time_out) return '';
        return this.hm(this.pm_time_out);
      }
    },
    created() {
      this.getGroups();
      this.getShifts();
      this.getAll();
    },
    methods: {
      hm(value) {
        if (!value) return '--';
        const [h0, m] = value.split(':');
        const h = parseInt(h0, 10);
        const ampm = h >= 12 ? 'pm' : 'am';
        const hh = h % 12 === 0 ? 12 : h % 12;
        return hh + ':' + String(m).padStart(2, '0') + ' ' + ampm;
      },
      getGroups() {
        this.axios.get('payroll-groups/options').then(response => {
          this.groups = response.data || [];
        }).catch(error => console.log(error));
      },
      getShifts() {
        this.axios.get('shift-types/options').then(response => {
          this.shifts = response.data || [];
        }).catch(error => console.log(error));
      },
      allTicked(g) {
        return g.shifts.every(s => this.form.shift_type_ids.includes(s.id));
      },
      toggleGroup(g) {
        const ids = g.shifts.map(s => s.id);
        if (this.allTicked(g)) {
          this.form.shift_type_ids = this.form.shift_type_ids.filter(id => !ids.includes(id));
        } else {
          this.form.shift_type_ids = Array.from(new Set(this.form.shift_type_ids.concat(ids)));
        }
        this.getEarliestStart();
      },
      // Earliest OT start for the ticked shifts on the chosen date, and default
      // the window to start there (three hours long) when adding.
      getEarliestStart() {
        this.$nextTick(() => {
          if (!this.form.shift_type_ids.length) {
            this.pm_time_out = '';
            return;
          }
          this.axios.get('office-ot-schedules/earliest-start', { params: { shift_type_ids: this.form.shift_type_ids.join(','), date: this.form.date || undefined } })
            .then(response => {
              this.pm_time_out = (response.data && response.data.pm_time_out) ? response.data.pm_time_out.substring(0, 5) : '';
              if (!this.form.id && this.pm_time_out) {
                const [h, m] = this.pm_time_out.split(':').map(Number);
                this.form.time_from = this.pm_time_out;
                this.form.time_to = String((h + 3) % 24).padStart(2, '0') + ':' + String(m).padStart(2, '0');
              }
            }).catch(error => console.log(error));
        });
      },
      getAll() {
        this.loading = true;
        this.axios.get('office-ot-schedules/all', { params: { page: this.list.current_page, per_page: 25, search: this.search, payroll_group_id: this.filter_group_id } })
          .then(response => { this.list = response.data; this.loading = false; })
          .catch(error => { console.log(error); this.loading = false; });
      },
      duration(item) {
        const [fh, fm] = item.time_from.split(':').map(Number);
        const [th, tm] = item.time_to.split(':').map(Number);
        let minutes = (th * 60 + tm) - (fh * 60 + fm);
        if (minutes <= 0) minutes += 24 * 60;
        const h = Math.floor(minutes / 60), m = minutes % 60;
        return (h ? h + ' hr' + (h > 1 ? 's' : '') : '') + (m ? ' ' + m + ' min' : '');
      },
      statusVariant(status) {
        if (status === 'Today') return 'success';
        if (status === 'Upcoming') return 'primary';
        return 'secondary';
      },
      openAdd() {
        // Pre-tick the shifts of the filtered group, if any.
        const preset = this.filter_group_id
          ? this.shifts.filter(s => s.payroll_group_id === this.filter_group_id).map(s => s.id)
          : [];
        this.form = { id: null, shift_type_ids: preset, date: '', time_from: '17:00', time_to: '20:00', purpose: '' };
        this.pm_time_out = '';
        this.modal = true;
        if (preset.length) this.getEarliestStart();
      },
      openEdit(item) {
        this.form = {
          id: item.id,
          shift_type_ids: (item.shift_types || []).map(s => s.id),
          date: (item.date || '').substring(0, 10),
          time_from: (item.time_from || '').substring(0, 5),
          time_to: (item.time_to || '').substring(0, 5),
          purpose: item.purpose
        };
        this.modal = true;
        this.getEarliestStart();
      },
      save() {
        this.axios.post('office-ot-schedules/save', this.form).then(() => {
          this.modal = false;
          this.swalTransactionCompleted();
          this.getAll();
        }).catch(error => {
          this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Something went wrong.';
          this.swalRequestError();
        });
      },
      remove(item) {
        this.swalConfirmDelete((data) => {
          if (data.value) {
            this.axios.delete('office-ot-schedules/delete', { data: { id: item.id } }).then(() => {
              this.swalTransactionCompleted();
              this.getAll();
            }).catch(() => {
              this.swalRequestError();
            });
          }
        });
      }
    }
  }
</script>

<style scoped>
  .shift-picker {
    max-height: 340px;
    overflow-y: auto;
    background: #f8f9fa;
  }
</style>
